import { useEffect, useState } from 'react';
import { Stack, Card, Group, Button, Badge, Text, Title, Skeleton, ActionIcon } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert, EmptyState, StatusBadge, KpiFilterRow, type KpiFilterItem } from '@repletoryreach/sdk-ui/data-display';
import { Switch, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import type { featureFlags } from '@repletoryreach/sdk-api';
import {
  useFeatureFlags,
  useUpsertFeatureFlag,
  useDeleteFeatureFlag,
  useDeleteOverride
} from '../../../data/useFeatureFlags';
import { EditFlagDrawer } from './components/EditFlagDrawer';
import { scopeName, tagTone, FLAG_SCOPES } from './scopes';
import { TenantName } from '../../../components/TenantName';
import { TableFooter } from '../../../components/TableFooter';
import { usePersistedState } from '../../../lib/usePersistedState';
import { formatDateTime, formatRelative } from '../../../lib/format';
import { useT, type Translate } from '../../../i18n/I18nProvider';
import './feature-flags.css';

type Kpi = 'all' | 'on' | 'off' | 'archived' | 'overrides';

const FlagIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M6 21V4" /><path d="M6 4.5h11l-2 3.5 2 3.5H6" />
  </svg>
);
const TrashIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M4 7h16M9.5 7V5h5v2M6.5 7l1 13h9l1-13" />
  </svg>
);

export default function FeatureFlagsPage() {
  const t = useT();
  const [showArchived, setShowArchived] = useState(false);

  /*
   * Archived flags are always fetched. The page used to ask the server for
   * live flags only, then count "Archived" over that list, so the card read 0
   * until the switch was flipped and the number it showed before that was a
   * lie. The switch is a display filter over one honest set of counts.
   */
  const flagsQ = useFeatureFlags(true);
  const upsert = useUpsertFeatureFlag();
  const del = useDeleteFeatureFlag();
  const delOverride = useDeleteOverride();
  const [editing, setEditing] = useState<featureFlags.FeatureFlag | 'new' | null>(null);

  const [query, setQuery] = useState('');
  const [kpi, setKpi] = useState<Kpi>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePersistedState<number>('admin.feature-flags.pageSize', 10);

  const all = flagsQ.data?.data ?? [];
  const failed = Boolean(flagsQ.error);

  const counts = {
    all: all.length,
    on: all.filter((f) => f.default_enabled && !f.archived).length,
    off: all.filter((f) => !f.default_enabled && !f.archived).length,
    archived: all.filter((f) => f.archived).length,
    overrides: all.filter((f) => f.overrides.length > 0).length
  };

  /*
   * A registry the query could not read is not an empty registry, so every
   * card says "n/a" rather than a zero that reads as "no flags".
   */
  const value = (n: number) => failed ? t('common.notAvailable') : n;
  const kpiItems: KpiFilterItem[] = [
    { id: 'all', label: t('settings.flags.kpi.all'), value: value(counts.all), tone: 'primary', icon: FlagIcon, hint: t('settings.flags.stats.totalHint') },
    { id: 'on', label: t('settings.flags.kpi.on'), value: value(counts.on), tone: 'healthy', hint: t('settings.flags.stats.onHint') },
    { id: 'off', label: t('settings.flags.kpi.off'), value: value(counts.off), tone: 'down' },
    { id: 'archived', label: t('settings.flags.kpi.archived'), value: value(counts.archived), tone: 'inactive' },
    { id: 'overrides', label: t('settings.flags.kpi.overrides'), value: value(counts.overrides), tone: 'warning', hint: t('settings.flags.stats.overriddenHint') }
  ];

  const filtered = (() => {
    let rows = all;
    // Asking for archived flags explicitly wins over the display switch, so a
    // card never leads to an empty list it cannot explain.
    if (!showArchived && kpi !== 'archived') rows = rows.filter((f) => !f.archived);
    if (kpi === 'on') rows = rows.filter((f) => f.default_enabled && !f.archived);
    if (kpi === 'off') rows = rows.filter((f) => !f.default_enabled && !f.archived);
    if (kpi === 'archived') rows = rows.filter((f) => f.archived);
    if (kpi === 'overrides') rows = rows.filter((f) => f.overrides.length > 0);
    const q = query.trim().toLowerCase();
    if (q) rows = rows.filter((f) =>
      f.key.toLowerCase().includes(q) ||
      (f.description ?? '').toLowerCase().includes(q) ||
      (f.owner ?? '').toLowerCase().includes(q)
    );
    return rows;
  })();

  useEffect(() => { if (page > 1 && (page - 1) * pageSize >= filtered.length) setPage(1); }, [filtered.length, page, pageSize]);
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);
  const filtering = Boolean(query.trim()) || kpi !== 'all';

  async function applyToggle(flag: featureFlags.FeatureFlag) {
    try {
      await upsert.mutateAsync({
        key: flag.key,
        description: flag.description,
        owner: flag.owner,
        default_enabled: !flag.default_enabled,
        rollout_percent: flag.rollout_percent,
        tags: flag.tags,
        archived: flag.archived
      });
      notifySuccess(
        t(!flag.default_enabled ? 'settings.flags.toast.toggledOn' : 'settings.flags.toast.toggledOff', { key: flag.key }),
        t('settings.flags.toast.toggledTitle')
      );
    } catch (e) {
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.flags.toast.toggleFailed'));
    }
  }

  /*
   * Toggling the default changes what every scope without an override sees, so
   * it confirms like Delete does. The body names the flag, the value it lands
   * on, and how many overrides will keep ignoring it, because "off for
   * everyone" and "off for everyone except these four tenants" are different
   * decisions.
   */
  function toggleDefault(flag: featureFlags.FeatureFlag) {
    const turningOff = flag.default_enabled;
    openConfirmModal({
      title: t('settings.flags.toggle.title', { key: flag.key }),
      children: (
        <p style={{ margin: 0 }}>
          {t(turningOff ? 'settings.flags.toggle.bodyOff' : 'settings.flags.toggle.bodyOn', { key: flag.key })}
          {' '}
          {flag.overrides.length > 0
            ? t('settings.flags.toggle.overridesKeep', { count: flag.overrides.length })
            : t('settings.flags.toggle.noOverrides')}
        </p>
      ),
      labels: {
        confirm: t(turningOff ? 'settings.flags.toggle.confirmOff' : 'settings.flags.toggle.confirmOn'),
        cancel: t('action.cancel')
      },
      confirmProps: turningOff ? { color: 'red' } : undefined,
      onConfirm: () => { void applyToggle(flag); }
    });
  }

  function deleteFlag(flag: featureFlags.FeatureFlag) {
    openConfirmModal({
      title: t('settings.flags.delete.title'),
      children: <p style={{ margin: 0 }}>{t('settings.flags.delete.body', { key: flag.key, count: flag.overrides.length })}</p>,
      labels: { confirm: t('action.delete'), cancel: t('action.cancel') },
      confirmProps: { color: 'red' },
      // Awaited, so a failed delete says so rather than closing the modal and
      // leaving the row on screen with no explanation.
      onConfirm: async () => {
        try {
          await del.mutateAsync(flag.key);
          notifySuccess(t('settings.flags.toast.deleted', { key: flag.key }), t('settings.flags.toast.deletedTitle'));
        } catch (e) {
          notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.flags.toast.deleteFailed'));
        }
      }
    });
  }

  /*
   * Removing an override was only possible by hand against the API: the page
   * listed overrides and offered no way to take one off, so a kill switch aimed
   * at one tenant during an incident stayed aimed at them afterwards.
   */
  function removeOverride(flag: featureFlags.FeatureFlag, override: featureFlags.FeatureFlagOverride) {
    openConfirmModal({
      title: t('settings.flags.overrides.removeTitle'),
      children: (
        <p style={{ margin: 0 }}>
          {t('settings.flags.overrides.removeBody', {
            scope: t(FLAG_SCOPES.find((s) => s.id === override.scope_id)?.labelKey ?? '', scopeName(override.scope_id)),
            key: flag.key,
            state: flag.default_enabled ? t('settings.flags.on') : t('settings.flags.off')
          })}
        </p>
      ),
      labels: { confirm: t('action.remove'), cancel: t('action.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await delOverride.mutateAsync({ key: flag.key, id: override.id });
          notifySuccess(t('settings.flags.overrides.toast.removed'), t('settings.flags.scopedOverrides'));
        } catch (e) {
          notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.flags.overrides.toast.removeFailed'));
        }
      }
    });
  }

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('common.settings')}
        title={t('settings.layout.nav.featureFlags')}
        description={t('settings.flags.description')}
        actions={
          <Group gap="sm">
            <Switch
              label={t('settings.flags.showArchived')}
              checked={showArchived}
              onChange={(next) => { setShowArchived(next); setPage(1); }}
            />
            <Button size="sm" onClick={() => setEditing('new')}>{t('settings.flags.new')}</Button>
          </Group>
        }
      />

      {flagsQ.error && (
        <Alert color="error">
          <Group justify="space-between" align="center" wrap="wrap">
            <Text role="bodySmall">{t('settings.flags.loadError')}</Text>
            <Button size="xs" variant="outline" onClick={() => void flagsQ.refetch()}>{t('action.retry')}</Button>
          </Group>
        </Alert>
      )}

      {/* The five cards are the five ways an operator slices this registry, so
          they filter it rather than only reporting on it. */}
      <KpiFilterRow
        items={kpiItems}
        value={kpi}
        onChange={(next) => { setKpi((next ?? 'all') as Kpi); setPage(1); }}
        filteringLabel={t('common.filtering')}
      />

      <TextInput
        value={query}
        onChange={(e) => { setQuery(e.currentTarget.value); setPage(1); }}
        placeholder={t('settings.flags.searchPlaceholder')}
        aria-label={t('common.search')}
      />

      {flagsQ.isLoading && (
        <Stack gap="md">
          <Skeleton height={104} />
          <Skeleton height={104} />
          <Skeleton height={104} />
        </Stack>
      )}

      <Stack gap="md">
        {filtered.length === 0 && !flagsQ.isLoading && !flagsQ.error && (
          <Card withBorder>
            <EmptyState
              title={filtering ? t('settings.flags.emptyMatch') : t('settings.flags.empty')}
              description={filtering ? t('settings.flags.emptyMatchHint') : t('settings.flags.emptyHint')}
              action={filtering
                ? <Button variant="outline" onClick={() => { setQuery(''); setKpi('all'); }}>{t('common.clearFilters')}</Button>
                : <Button onClick={() => setEditing('new')}>{t('settings.flags.createFirst')}</Button>}
            />
          </Card>
        )}

        {paged.map((flag) => (
          <Card key={flag.key} withBorder>
            <Stack gap="md">
              <Group justify="space-between" align="flex-start" wrap="wrap">
                <Stack gap="xs">
                  <Group gap="xs" align="center">
                    <Title order={5}>{flag.key}</Title>
                    {flag.archived && <Badge size="xs" color="gray">{t('settings.flags.archived')}</Badge>}
                    {flag.tags.map((tag) => (
                      <Badge key={tag} size="xs" variant="light" color={tagTone(tag)}>{tag}</Badge>
                    ))}
                  </Group>
                  {flag.description && (
                    <Text role="bodySmall" c="var(--rr-color-text-muted)">{flag.description}</Text>
                  )}
                  <Text role="caption" c="var(--rr-color-text-muted)">
                    {flag.owner ? t('settings.flags.owner', { owner: flag.owner }) : t('settings.flags.noOwner')} ·{' '}
                    {t('settings.flags.rollout', { percent: flag.rollout_percent })} ·{' '}
                    {t(flag.overrides.length === 1 ? 'settings.flags.overridesOne' : 'settings.flags.overridesOther', { count: flag.overrides.length })}
                  </Text>
                  {/* The first question after an incident is who flipped this
                      and when. Both fields were on every payload already. */}
                  <Text role="caption" c="var(--rr-color-text-muted)" title={formatDateTime(flag.updated_at)}>
                    {flag.updated_by
                      ? t('settings.flags.lastChangedBy', { when: formatRelative(flag.updated_at), who: flag.updated_by })
                      : t('settings.flags.lastChanged', { when: formatRelative(flag.updated_at) })}
                  </Text>
                </Stack>
                <Group gap="sm" wrap="wrap">
                  <StatusBadge status={flag.default_enabled ? 'healthy' : 'inactive'}>
                    {t('settings.flags.defaultState', { state: flag.default_enabled ? t('settings.flags.on') : t('settings.flags.off') })}
                  </StatusBadge>
                  <Button
                    size="xs"
                    variant="default"
                    loading={upsert.isPending && upsert.variables?.key === flag.key}
                    onClick={() => toggleDefault(flag)}
                  >
                    {t('settings.flags.toggleDefault')}
                  </Button>
                  <Button size="xs" onClick={() => setEditing(flag)}>{t('action.edit')}</Button>
                  <Button size="xs" variant="subtle" color="red" onClick={() => deleteFlag(flag)}>
                    {t('action.delete')}
                  </Button>
                </Group>
              </Group>

              {flag.overrides.length > 0 && (
                <div className="rr-flag-overrides">
                  <div className="rr-flag-overrides__head">
                    <Text role="bodySmall" fw={600}>
                      {t('settings.flags.overrides.title', { count: flag.overrides.length })}
                    </Text>
                  </div>
                  <ul className="rr-flag-overrides__list">
                    {flag.overrides.map((o) => (
                      <li key={o.id}>
                        <div className="rr-flag-overrides__row">
                          <span className="rr-flag-overrides__target">
                            <OverrideTarget override={o} t={t} />
                            <Text role="caption" c="var(--rr-color-text-muted)" aria-hidden>→</Text>
                            <StatusBadge status={o.enabled ? 'healthy' : 'inactive'}>
                              {o.enabled ? t('settings.flags.on') : t('settings.flags.off')}
                            </StatusBadge>
                            {o.reason && <Text role="caption" c="var(--rr-color-text-muted)">{o.reason}</Text>}
                          </span>
                          <span className="rr-flag-overrides__when" title={formatDateTime(o.created_at)}>
                            {o.created_by
                              ? t('settings.flags.overrides.updatedBy', { when: formatRelative(o.created_at), who: o.created_by })
                              : t('settings.flags.overrides.updated', { when: formatRelative(o.created_at) })}
                          </span>
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            color="error"
                            aria-label={t('settings.flags.overrides.remove')}
                            onClick={() => removeOverride(flag, o)}
                          >
                            {TrashIcon}
                          </ActionIcon>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Stack>
          </Card>
        ))}
      </Stack>

      {filtered.length > 0 && (
        <TableFooter
          page={page}
          pageSize={pageSize}
          total={filtered.length}
          onPageChange={setPage}
          onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
        />
      )}

      <EditFlagDrawer
        flag={editing}
        onClose={() => setEditing(null)}
      />
    </Stack>
  );
}

/**
 * What the override applies to, in the operator's words.
 *
 * An org-scoped override stores an organisation UUID. Printing that raw was the
 * one thing this page could not do and still be read: `org:0192f3a1-…` names
 * nothing. The tenants cache turns it into the organisation's name; every other
 * scope stores a value a person genuinely quotes (a country code, a msisdn), so
 * those print as they are, behind their scope label.
 */
function OverrideTarget({ override, t }: { override: featureFlags.FeatureFlagOverride; t: Translate }) {
  const scope = FLAG_SCOPES.find((s) => s.id === override.scope_id);
  const label = scope ? t(scope.labelKey) : scopeName(override.scope_id);
  return (
    <Group gap="xs" align="center" wrap="nowrap" style={{ minWidth: 0 }}>
      <Badge size="xs" variant="light">{label}</Badge>
      {scope?.code === 'org'
        ? <TenantName id={override.scope_target} />
        : <Text role="bodySmall">{override.scope_target}</Text>}
    </Group>
  );
}
